import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Accessibility Service — WCAG 2.2 AA / AAA Compliance
 */
@Injectable({
  providedIn: 'root'
})
export class A11yService {

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.initializeA11y();
  }

  /**
   * Initialize accessibility features
   */
  private initializeA11y(): void {
    this.setAriaLabels();
    this.enhanceKeyboardNavigation();
    this.improveColorContrast();
    this.ensureFormAccessibility();
    this.setupScreenReaderSupport();
  }

  /**
   * Set ARIA labels on interactive elements
   */
  private setAriaLabels(): void {
    // Buttons
    document.querySelectorAll('button:not([aria-label])').forEach(button => {
      if (!button.textContent?.trim()) {
        const icon = button.querySelector('i');
        if (icon) {
          const ariaLabel = this.getAriaLabelFromIcon(icon);
          button.setAttribute('aria-label', ariaLabel);
        }
      }
    });

    // Links
    document.querySelectorAll('a[href*="#"]:not([aria-label])').forEach(link => {
      if (!link.textContent?.trim()) {
        link.setAttribute('aria-label', 'Navigation link');
      }
    });

    // Icons inside interactive triggers
    document.querySelectorAll('i[class*="fa-"]:not([aria-hidden])').forEach(icon => {
      const parent = icon.parentElement;
      if (parent?.tagName === 'BUTTON' || parent?.tagName === 'A') {
        icon.setAttribute('aria-hidden', 'true');
      }
    });
  }

  /**
   * Get ARIA label from icon class
   */
  private getAriaLabelFromIcon(icon: Element): string {
    const classList = icon.className;
    const iconType = classList.replace(/fa.*?(fa-\w+).*?/, '$1').replace('fa-', '');

    const labels: Record<string, string> = {
      'search': 'Search',
      'menu': 'Menu',
      'close': 'Close',
      'chevron-down': 'Expand',
      'chevron-up': 'Collapse',
      'phone': 'Phone',
      'envelope': 'Email',
      'linkedin': 'LinkedIn',
      'twitter': 'Twitter',
      'github': 'GitHub',
      'facebook': 'Facebook',
      'instagram': 'Instagram'
    };

    return labels[iconType] || 'Button';
  }

  /**
   * Enhance keyboard navigation
   */
  private enhanceKeyboardNavigation(): void {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        document.body.classList.add('keyboard-nav');
      }
    });

    document.addEventListener('mousedown', () => {
      document.body.classList.remove('keyboard-nav');
    });

    // Ensure interactive elements carry tabindex
    document.querySelectorAll('[role="button"]').forEach(element => {
      if (!element.hasAttribute('tabindex')) {
        element.setAttribute('tabindex', '0');
      }

      element.addEventListener('keydown', (e) => {
        const keyEvent = e as KeyboardEvent;
        if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
          keyEvent.preventDefault();
          (element as HTMLElement).click();
        }
      });
    });
  }

  /**
   * Improve color contrast check — filters visible text elements only
   */
  private improveColorContrast(): void {
    const textElements = document.querySelectorAll('h1, h2, h3, h4, h5, h6, p, a, button, label, span, strong, em, li, td, th');

    textElements.forEach(element => {
      if (element.tagName === 'SCRIPT' || element.tagName === 'STYLE' || element.tagName === 'SVG' || !element.textContent?.trim()) {
        return;
      }

      const style = window.getComputedStyle(element);
      const bgColor = style.backgroundColor;
      const color = style.color;

      if (!bgColor || !color || bgColor === 'transparent' || bgColor === 'rgba(0, 0, 0, 0)') {
        return;
      }

      const contrast = this.getContrastRatio(bgColor, color);

      if (contrast < 4.5 && contrast > 1.0) {
        console.warn(`Low contrast on element:`, element, `Contrast ratio: ${contrast.toFixed(2)}`);
      }
    });
  }

  /**
   * Calculate contrast ratio between two colors
   */
  private getContrastRatio(color1: string, color2: string): number {
    const luminance1 = this.getLuminance(color1);
    const luminance2 = this.getLuminance(color2);

    const lighter = Math.max(luminance1, luminance2);
    const darker = Math.min(luminance1, luminance2);

    return (lighter + 0.05) / (darker + 0.05);
  }

  /**
   * Get luminance of a color
   */
  private getLuminance(color: string): number {
    const rgb = this.parseColor(color);
    if (!rgb) return 0.5;

    const [r, g, b] = rgb.map(val => {
      val = val / 255;
      return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  /**
   * Parse RGB color string
   */
  private parseColor(color: string): number[] | null {
    const rgb = color.match(/\d+/g);
    return rgb ? rgb.map(Number) : null;
  }

  /**
   * Ensure form accessibility
   */
  private ensureFormAccessibility(): void {
    document.querySelectorAll('input, textarea, select').forEach(input => {
      if (!input.id) {
        const randId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID().substring(0, 8) : Math.random().toString(36).substring(2, 9);
        input.id = `input-${randId}`;
      }

      const label = document.querySelector(`label[for="${input.id}"]`);
      if (!label && input.getAttribute('aria-label') === null) {
        const placeholder = input.getAttribute('placeholder');
        if (placeholder) {
          input.setAttribute('aria-label', placeholder);
        }
      }
    });

    document.querySelectorAll('.error-message, .form-feedback.error').forEach(error => {
      if (!error.getAttribute('role')) {
        error.setAttribute('role', 'alert');
        error.setAttribute('aria-live', 'assertive');
      }
    });
  }

  /**
   * Setup screen reader support
   */
  private setupScreenReaderSupport(): void {
    if (!document.querySelector('.skip-link')) {
      const skipLink = document.createElement('a');
      skipLink.href = '#main-content';
      skipLink.className = 'skip-link sr-only';
      skipLink.textContent = 'Skip to main content';
      skipLink.tabIndex = 0;

      const body = document.querySelector('body');
      if (body) {
        body.prepend(skipLink);
      }
    }

    const main = document.querySelector('main');
    if (!main) {
      console.warn('Page should have a <main> element for accessibility');
    }

    this.validateHeadingHierarchy();
  }

  /**
   * Validate proper heading hierarchy (H1 before H2, etc.)
   */
  private validateHeadingHierarchy(): void {
    const headings = Array.from(document.querySelectorAll('h1, h2, h3, h4, h5, h6'));
    let lastLevel = 0;

    headings.forEach((heading, index) => {
      const level = Number.parseInt(heading.tagName.substring(1), 10);

      if (index === 0 && level !== 1) {
        console.warn('Page should start with H1:', heading);
      }

      if (level > lastLevel + 1 && lastLevel !== 0) {
        console.warn(`Heading level skip from H${lastLevel} to H${level}:`, heading);
      }

      lastLevel = level;
    });
  }

  /**
   * Add focus management
   */
  public focusElement(element: HTMLElement): void {
    element.focus();
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Announce message to screen readers
   */
  public announce(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
      announcement.remove();
    }, 1000);
  }

  /**
   * Check WCAG AA compliance
   */
  public checkWCAGCompliance(): WCAGReport {
    const issues: string[] = [];

    document.querySelectorAll('img').forEach(img => {
      if (!img.alt && !img.getAttribute('aria-label') && img.getAttribute('aria-hidden') !== 'true') {
        issues.push(`Image missing alt text: ${img.src}`);
      }
    });

    const h1Count = document.querySelectorAll('h1').length;
    if (h1Count !== 1) {
      issues.push(`Page should have exactly 1 H1, found ${h1Count}`);
    }

    document.querySelectorAll('input, textarea, select').forEach(input => {
      const htmlInput = input as HTMLInputElement | HTMLTextAreaElement;
      if (!input.parentElement?.querySelector('label') && !input.getAttribute('aria-label') && !document.querySelector(`label[for="${input.id}"]`)) {
        issues.push(`Form input missing label: ${htmlInput.name || htmlInput.id}`);
      }
    });

    const contrastIssues: string[] = [];
    document.querySelectorAll('h1, h2, h3, h4, h5, h6, p, a, button, label, span').forEach(element => {
      if (!element.textContent?.trim()) return;
      const style = window.getComputedStyle(element);
      if (style.backgroundColor && style.color && style.backgroundColor !== 'transparent') {
        const contrast = this.getContrastRatio(style.backgroundColor, style.color);
        if (contrast < 4.5 && contrast > 1.0) {
          contrastIssues.push(`Low contrast: ${contrast.toFixed(2)}:1 on <${element.tagName.toLowerCase()}>`);
        }
      }
    });

    return {
      score: Math.max(0, 100 - (issues.length * 5 + contrastIssues.length * 3)),
      passed: issues.length === 0 && contrastIssues.length === 0,
      issues,
      contrastIssues,
      warnings: []
    };
  }
}

export interface WCAGReport {
  score: number;
  passed: boolean;
  issues: string[];
  contrastIssues: string[];
  warnings: string[];
}
